import React, { useEffect, useState, useContext } from "react";
import { Layout, Menu, Dropdown, Avatar, Badge, Spin, Popover, List, Divider, Row } from "antd";
import { BellOutlined, UserOutlined, CaretDownOutlined, LoadingOutlined, MailOutlined } from "@ant-design/icons";
import "./navbar.scss";
import { PAGE_ROUTES } from "../../utils/constants";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppSecureStorage from "../../services/secureStorage";
import logo from "../../assets/logo.svg";
import { getAllNotificationData } from "../../redux/slices/trainAndContentSlice";
import moment from 'moment';
import { ThemeContext } from '../../styles/themeProvider';
import { isProfileGet, isTrainAndContentNotification } from '../../utils/permissions';
import { capitalize } from 'lodash';

const { Header } = Layout;

const storage = new AppSecureStorage();

const AppHeader = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, profileLoading } = useSelector((state) => state.profileReducer);
  const { allNotifications, firebaseNotification } = useSelector((state) => state.trainAndContentReducer);
  const [notifications, setNotifications] = useState([]);
  const { reverseTheme, toggleTheme } = useContext(ThemeContext); 
  const [lastSeenTime, setLastSeenTime] = useState(
    storage.get("lastSeenNotificationTime")|| null
  );
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  useEffect(() => {
    if (isTrainAndContentNotification) {
      dispatch(getAllNotificationData({ navigate }));
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    let formattedNotifications = [];
    if (firebaseNotification?.title === "Training document upload") {
      formattedNotifications.push({
        avatar: user?.picture,
        title: firebaseNotification.title,
        description: firebaseNotification.body,
        time: moment().format("MMM DD, YYYY, h:mm A"),
        date: moment()
      });
    }

    if (allNotifications?.length) {
      const otherNotifications = allNotifications.map((notification) => ({
        avatar: user?.picture,
        title: notification.title,
        description: notification.body,
        time: moment(notification.dateSent).format("MMM DD, YYYY, h:mm A"),
        date: moment(notification.dateSent)
      }));

      formattedNotifications = [...formattedNotifications, ...otherNotifications];
    }
    formattedNotifications.sort((a, b) => b.date - a.date);

    const _todayNotifications = formattedNotifications.filter((notification) => moment(notification.date).isSame(moment(), 'day'));
    const _previousNotifications = formattedNotifications.filter((notification) => !moment(notification.date).isSame(moment(), 'day'));
    const _notifications = [
      ...(_todayNotifications.length ? [{ header: "Today" }] : []),
      ..._todayNotifications,
      ...(_previousNotifications.length ? [{ header: "Previous" }] : []),
      ..._previousNotifications
    ];
     const hasUnread = formattedNotifications.some(notif => 
    !notif.header && 
    (!lastSeenTime || moment(notif.date).isAfter(lastSeenTime))
  );

    setHasUnreadNotifications(hasUnread);
    setNotifications(_notifications);
  }, [allNotifications, firebaseNotification, user]);

  const handleLogout = () => {
    storage.clearAuthData(); 
    navigate("/");
  };
  const handleNotificationOpen = (open) => {
  if (open) {
    const now = moment().toISOString();
    setLastSeenTime(now);
    storage.set('lastSeenNotificationTime', now);
    setHasUnreadNotifications(false);
  }
};

  const menu = (
    <Menu>
      {isProfileGet &&
        <Menu.Item key="1" onClick={() => navigate(PAGE_ROUTES.SETTINGS)}>
          Settings
        </Menu.Item>
      }
      <Menu.Item key="2" onClick={toggleTheme}>
        {capitalize(`${reverseTheme} mode`)}
      </Menu.Item>
      <Menu.Item key="3" onClick={handleLogout}>
        Logout
      </Menu.Item>
    </Menu>
  );
  const notificationPopover = (
    <div className="notification-container">
      <div className="notification-header">
        <h3>Notifications</h3>
      </div>
      <List
        dataSource={notifications}
        renderItem={(item) =>
          item.header ?
            (
              <Divider plain style={{ borderColor: "#cfcfcf" }}>
                {item.header}
              </Divider>
            ) :
            (
              <List.Item className="notification-item">
                <List.Item.Meta
                  avatar={
                   <Avatar icon={<MailOutlined />} />
                  }
                  title={<span>{item.title}</span>}
                  description={
                    <>
                      <div>{item.description}</div>
                      <div className="notification-time">{item.time}</div>
                    </>
                  }
                />
              </List.Item>
            )
        }
      />
    </div>
  );

  return (
    <Header className="site-layout-background">
      <div className="header-content">
        <div className="header-left">
          <img src={logo} alt="Logo" className="header-logo" />
          <div className="header-text">
            <h1>SimplifyPath</h1>
            <a
              href="https://www.tftus.com/"
              className="powered-by"
              target="_blank"
              rel="noopener noreferrer"
            >
              Powered by TFT
            </a>
          </div>
          <Divider type="vertical" style={{ borderWidth: 1.75, height: 40, marginRight: 20, marginLeft: 20 }} />
          <Row align={"middle"}>
            <Avatar
              size={32}
              src={
                profileLoading ? (
                  <Spin indicator={<LoadingOutlined spin />} size="large" />
                ) : (
                  user?.organisationLogo
                )
              }
              className="organisation-logo"
            />
            <div className="organisation-name">{user?.organisationName}</div>
          </Row>
        </div>
        <div className="header-right">
          {
            isTrainAndContentNotification &&
            <Popover
              content={notificationPopover}
              trigger="click"
              placement="bottomRight"
              overlayClassName="custom-notification-popover"
              onOpenChange={handleNotificationOpen}
            >
              <Badge
                dot={hasUnreadNotifications}
                offset={[10, 0]}
                style={{ marginRight: "25px" }}
              >
                <BellOutlined className="header-icon" />
              </Badge>
            </Popover>
          }
          <Dropdown overlay={menu} trigger={["click"]}>
            <div className="avatar-dropdown">
              <Avatar
                src={
                  profileLoading ? (
                    <Spin indicator={<LoadingOutlined spin />} size="default" />
                  ) : (
                    user?.picture && user?.picture
                  )
                }
                icon={!user?.picture && <UserOutlined />}
                className="header-icon"
              />
              <CaretDownOutlined className="dropdown-icon" />
            </div>
          </Dropdown>
        </div>
      </div>
    </Header>
  );
};

export default AppHeader;

